import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import { TextInput } from '@common/components/form/inputs/text/text-input';
import { Select } from '@common/components/form/inputs/select/select-input';
import { TrialArrow } from './trial-arrow';
import type { EditableLocation } from './locations-editor';
import {
  type TrialBrandColors,
  type TrialModuleKey,
  type TrialResearchPreview,
} from '../../types';
import {
  clampTrialIndustryInput,
  isTrialIndustryInputValid,
  MAX_TRIAL_INDUSTRY_CHAR_LENGTH,
  trialIndustryInputError,
} from '../../utils/trial-industry';

export type PreviewValues = {
  name: string;
  industry: string;
  languageLocale: 'nl' | 'en';
  locations: EditableLocation[];
  modules: TrialModuleKey[];
  colors: TrialBrandColors;
  logoUrl?: string | null;
  logoFile?: File | null;
};

export const toEditableLocations = (
  preview: TrialResearchPreview,
): EditableLocation[] => preview.locations.map((location, index) => ({
  id: location.place_id ?? `found-${index}`,
  place_id: location.place_id,
  name: location.name,
  city: location.city,
  is_head_office: location.is_head_office,
  selected: true,
}));

type Props = {
  values: PreviewValues;
  onChange: (values: PreviewValues) => void;
  onContinue: () => void;
  onReject: () => void;
};

export const PreviewStep = ({
  values, onChange, onContinue, onReject,
}: Props) => {
  const { t } = useTranslation();
  const languageOptions = [
    { value: 'nl', label: 'Nederlands' },
    { value: 'en', label: 'English' },
  ];
  const industryError = trialIndustryInputError(values.industry, t);
  const canContinue = values.name.trim() && isTrialIndustryInputValid(values.industry);

  return (
    <form
      className={
        'FreeTrial__Glass max-md:!tw-mb-0 max-md:!tw-w-full max-md:!tw-max-h-[calc(100dvh-96px)] '
        + 'max-md:!tw-overflow-y-auto max-md:!tw-rounded-b-none max-md:!tw-rounded-t-[28px] max-md:!tw-border-0'
      }
      onSubmit={(e) => {
        e.preventDefault();
        if (canContinue) onContinue();
      }}
    >
      <div className="FreeTrial__Glass__Head">
        <p>{t('authentication:trial_setup_eyebrow', { current: 1, total: 3 })}</p>
        <h1>{t('authentication:trial_company_title', { name: values.name })}</h1>
      </div>
      <div className="FreeTrial__Field">
        <label htmlFor="trial-name">{t('authentication:trial_preview_company_name')}</label>
        <TextInput
          id="trial-name"
          value={values.name}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
          error={!values.name.trim()}
        />
      </div>
      <div className="FreeTrial__FieldGrid">
        <div className="FreeTrial__Field">
          <label htmlFor="trial-industry">{t('authentication:trial_preview_industry')}</label>
          <TextInput
            id="trial-industry"
            value={values.industry}
            maxLength={MAX_TRIAL_INDUSTRY_CHAR_LENGTH}
            hideMaxLengthCounter
            placeholder={t('authentication:trial_preview_industry_hint')}
            onChange={(e) => onChange({
              ...values,
              industry: clampTrialIndustryInput(e.target.value),
            })}
            error={industryError}
          />
        </div>
        <div className="FreeTrial__Field">
          <label htmlFor="trial-language">{t('authentication:trial_preview_language')}</label>
          <Select
            inputProps={{ id: 'trial-language' }}
            value={values.languageLocale}
            options={languageOptions}
            onChange={(option) => onChange({
              ...values,
              languageLocale: option?.value ?? values.languageLocale,
            })}
            clearable={false}
            searchable={false}
          />
        </div>
      </div>
      <Button type="primary" size="fill" buttonType="submit" disabled={!canContinue}>
        <span className="FreeTrial__Cta">
          <Trans i18nKey="authentication:trial_preview_continue" />
          <TrialArrow />
        </span>
      </Button>
      <a className="FreeTrial__GhostLink" onClick={onReject} role="link">{t('authentication:trial_preview_not_my_company')}</a>
    </form>
  );
};
